import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api'
import { takeLobbySeat, type TakeSeatRequest } from '../lib/takeSeat'
import type {
  AddBotRequest,
  AdminAiTakeoverPreview,
  GameResponse,
  CreateGameRequest,
  JoinGameRequest,
  ClaimRequest,
  ReinforceRequest,
  PostElimPlaceRequest,
  AttackRequest,
  AttackResponse,
  FortifyRequest,
  TransferRequest,
  TurnInCardsRequest,
  LobbyGamesResponse,
  PreviewGameResponse,
  JoinGameByIdRequest,
  UpdateMySlotRequest,
  UpdateMySlotResponse,
  UpdateMyColorRequest,
  UpdateMyColorResponse,
  LeaveGameResponse,
  RedeemInviteCodeRequest,
  UpdateGameSettingsRequest,
  RedeemInviteCodeResponse,
  GameConfig,
  ReturnToAttackResponse,
} from '@warcarp/api-types'

export function useBootPlayer(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => apiPost<{ game: GameResponse }>(`/api/v1/games/${gameId}/players/${userId}/boot`, {}),
    onSuccess: ({ game }) => { setGameCache(qc, gameId, game); void qc.invalidateQueries({ queryKey: QK.list }) },
  })
}

export function useBootPreview(gameId: string, userId: string | null) {
  return useQuery({
    queryKey: ['games', gameId, 'boot-preview', userId],
    queryFn: () => apiGet<AdminAiTakeoverPreview>(`/api/v1/games/${gameId}/players/${userId}/boot/preview`),
    enabled: !!gameId && !!userId,
    retry: false,
  })
}
import { reconcileGameFrame } from '../lib/reconcileGameFrame'
import { refreshTurnGames } from '../lib/refreshTurnGames'
import { gameRefetchInterval } from '../lib/gameLoadError'

const QK = {
  list: ['games'] as const,
  detail: (id: string) => ['games', id] as const,
  public: ['games', 'public'] as const,
  preview: (code: string) => ['games', 'preview', code] as const,
}

/** Write a game response without allowing a delayed response to roll back the
 * cache. Every HTTP mutation uses the same monotonic reconciliation as WS. */
function setGameCache(
  qc: ReturnType<typeof useQueryClient>,
  gameId: string,
  incoming: GameResponse,
) {
  qc.setQueryData<GameResponse>(QK.detail(gameId), (current) =>
    reconcileGameFrame(current, incoming),
  )
  refreshTurnGames(qc, qc.getQueryData<GameResponse>(QK.detail(gameId))!)
}

export function useGames(opts?: {
  /** Poll interval (ms) while mounted. Used by the global turn watcher so it
   *  notices turn changes in other games without relying on push. */
  refetchInterval?: number | false
}) {
  return useQuery({
    queryKey: QK.list,
    queryFn: () => apiGet<GameResponse[]>('/api/v1/games'),
    staleTime: 30_000,
    refetchInterval: opts?.refetchInterval,
    // Don't keep polling when the app is backgrounded.
    refetchIntervalInBackground: false,
  })
}

export function useGame(
  id: string | undefined,
  opts?: {
    /**
     * Pass the WebSocket `connected` boolean here. When true the poll is
     * suppressed (the socket already pushes state). When false/undefined the
     * hook falls back to a 15-second poll so the player still gets updates on
     * a flaky connection.
     */
    wsConnected?: boolean
  },
) {
  return useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => apiGet<GameResponse>(`/api/v1/games/${id}`),
    enabled: !!id,
    // Poll as fallback when the WebSocket is disconnected. Suppressed while
    // the socket is live — GAME_UPDATED / GAME_STATE_PATCH already push state
    // via qc.setQueryData, so polling would be redundant work. Also suppressed
    // on a definitive 403/404: those don't recover by polling (they need a
    // redeem or navigation), so hammering the endpoint every 15s is pointless.
    refetchInterval: (query) => gameRefetchInterval(query.state.error, !!opts?.wsConnected),
    // 10 s staleTime controls ordinary mount/refetch behavior. The root
    // AppLifecycleHandler explicitly refreshes active queries on foreground,
    // including fresh data, so notification-driven resumes do not wait for
    // staleTime.
    staleTime: 10_000,
    // Keep the live event feed accumulating across refetches, polls, AND
    // WS reconnects. A plain refetch returns only the recent wire tail; without
    // this merge the "Recent activity" feed would clear and reload every time
    // the socket drops and React Query refetches. mergeWireEvents unions the
    // append-only event log by absolute position (see the lib for details).
    structuralSharing: (prev, next) => {
      const p = prev as GameResponse | undefined
      const n = next as GameResponse
      return reconcileGameFrame(p, n) as unknown as typeof next
    },
  })
}

export function useCreateGame() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateGameRequest) =>
      apiPost<GameResponse>('/api/v1/games', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list }),
  })
}

/**
 * useLobbyGames — cursor-paginated infinite query for joinable LOBBY games.
 *
 * Each page is fetched from `GET /api/v1/games/lobby?cursor=<id>&limit=20`.
 * The backend returns public lobbies excluding games already joined or
 * pending invitation. Those games stay in the player's own home list.
 */
export function useLobbyGames(options?: { limit?: number; refetchInterval?: number | false }) {
  const limit = options?.limit ?? 20
  return useInfiniteQuery({
    queryKey: [...QK.public, limit],
    queryFn: ({ pageParam }) => {
      const cursor = pageParam as string | undefined
      const params = new URLSearchParams({ limit: String(limit) })
      if (cursor) params.set('cursor', cursor)
      return apiGet<LobbyGamesResponse>(`/api/v1/games/lobby?${params.toString()}`)
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30_000,
    refetchInterval: options?.refetchInterval,
  })
}

/**
 * usePreviewGameByCode — fetch a public game preview without authentication.
 *
 * Fires GET /api/v1/games/by-code/:code once during normal navigation. The
 * per-query window-focus refetch is disabled because the preview is static
 * enough; the root lifecycle refresh still covers an active notification
 * resume. Disabled when code is null/empty.
 */
export function usePreviewGameByCode(
  code: string | null,
  options?: {
    /** Polling interval override. Pass a function for conditional polling. */
    refetchInterval?: number | false | ((query: { state: { data: PreviewGameResponse | undefined } }) => number | false)
  },
) {
  return useQuery({
    queryKey: QK.preview(code ?? ''),
    queryFn: () => apiGet<PreviewGameResponse>(`/api/v1/games/by-code/${encodeURIComponent(code!)}`),
    enabled: !!code,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    ...(options?.refetchInterval !== undefined ? { refetchInterval: options.refetchInterval as never } : {}),
  })
}

export function useJoinGame() {
  const qc = useQueryClient()
  return useMutation({
    /**
     * Accepts either:
     *  - legacy { inviteCode } — auto-assigned slot (no slot param)
     *  - new { inviteCode, slot? } — optional slot selection from the picker
     *
     * The wire type for the legacy join-by-code route is JoinGameRequest.
     * The new slot-aware route POSTs to /games/:id/join with JoinGameByIdRequest.
     * This overload handles both cases:
     *   - When only inviteCode is supplied (or slot is omitted), we fall back to
     *     the legacy /games/join endpoint.
     *   - When gameId + slot are supplied, we use /games/:id/join.
     */
    mutationFn: (data: JoinGameRequest | ({ gameId: string } & JoinGameByIdRequest)) => {
      if ('gameId' in data) {
        const { gameId, slot, color } = data
        // Seat is normally auto-assigned; color is the player's chosen palette
        // slot (decoupled from seat). Send only the fields that are present.
        const body: JoinGameByIdRequest = {}
        if (slot != null) body.slot = slot
        if (color != null) body.color = color
        return apiPost<GameResponse>(`/api/v1/games/${gameId}/join`, body)
      }
      return apiPost<GameResponse>('/api/v1/games/join', data as JoinGameRequest)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list }),
  })
}

/**
 * useTakeSeat — take a lobby seat ("Take this seat", the invitee's "Join
 * game", and the join-by-code screen). Redeems the code first for a private
 * game reached by code, then POST /:id/join { slot?, color? }; see
 * lib/takeSeat.ts. The response lands in the detail cache so the lobby flips
 * to the seated view without waiting for a poll (non-members have no socket).
 */
export function useTakeSeat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: TakeSeatRequest) => takeLobbySeat(req, {
      redeem: (gameId, code) => apiPost<RedeemInviteCodeResponse>(`/api/v1/games/${gameId}/redeem-code`, { code }),
      join: ({ gameId, ...body }) => apiPost<GameResponse>(`/api/v1/games/${gameId}/join`, body),
    }),
    onSuccess: (game) => {
      setGameCache(qc, game.id, game)
      // The join response carries no viewer-specific fields; refetch them.
      void qc.invalidateQueries({ queryKey: QK.detail(game.id) })
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

/**
 * useUpdateMySlot — PATCH /api/v1/games/:id/me/slot { slot }
 *
 * Lets a player change their color while the game is still in LOBBY.
 * Invalidates the game detail query on success so the lobby re-renders.
 * On 409 SLOT_TAKEN, the caller should surface a toast and refetch the game.
 */
/**
 * useLeaveGame — POST /api/v1/games/:id/leave
 *
 * Lets a player (non-host) leave a LOBBY game.
 * On success, invalidates the game detail + the list + the preview-by-code
 * query so the unified /games/[id] LOBBY screen re-renders into State B
 * (slot picker) without a navigation event. The caller is responsible for
 * surfacing errors — this hook does NOT toast on 4xx.
 *
 * Error shape reference:
 *   403 HOST_CANNOT_LEAVE — host must cancel instead.
 *   400 GAME_STARTED      — can't leave a game that's already started.
 *   404 NOT_IN_GAME       — caller isn't a player in this game.
 */
export function useLeaveGame(gameId: string, inviteCode?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost<LeaveGameResponse>(`/api/v1/games/${gameId}/leave`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.detail(gameId) })
      void qc.invalidateQueries({ queryKey: QK.list })
      if (inviteCode) {
        void qc.invalidateQueries({ queryKey: QK.preview(inviteCode) })
      }
    },
  })
}

/**
 * useRemovePlayer — DELETE /api/v1/games/:id/players/:playerId
 *
 * Host-only LOBBY action that drops a joined player. The mutation
 * accepts the GamePlayer.id (not the user id) so bot stubs and
 * real users use the same path.
 */
export function useRemovePlayer(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (playerId: string) =>
      apiDelete<GameResponse>(`/api/v1/games/${gameId}/players/${playerId}`),
    onSuccess: (game) => {
      setGameCache(qc, gameId, game)
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

/**
 * useDeleteGame — DELETE /api/v1/games/:id
 *
 * Host-only LOBBY cancellation. The host taps "Delete Game", confirms,
 * and the entire game record is removed (players + invitations cascade).
 * Mobile callers navigate the host back to /games on success.
 */
export function useDeleteGame(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiDelete<{ message: string; gameId: string }>(`/api/v1/games/${gameId}`),
    onSuccess: () => {
      qc.removeQueries({ queryKey: QK.detail(gameId) })
      void qc.invalidateQueries({ queryKey: QK.list })
      void qc.invalidateQueries({ queryKey: QK.public })
    },
  })
}

/**
 * useUpdateGameSetup — PATCH /api/v1/games/:id/setup
 *
 * Host-only LOBBY setup edits. Body is `{ mapId?, config? }` — both
 * optional, server merges the config with the existing setupConfig
 * so callers can patch a single field.
 */
export function useUpdateGameSetup(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name?: string; mapId?: string; config?: Partial<GameConfig> }) =>
      apiPatch<GameResponse>(`/api/v1/games/${gameId}/setup`, body),
    onSuccess: (game) => {
      setGameCache(qc, gameId, game)
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

/**
 * useUpdateGameSettings — PATCH /api/v1/games/:id/settings
 *
 * Host-only LOBBY edits to maxPlayers + autoStart (Game columns, distinct from
 * GameConfig). Server rejects lowering maxPlayers below occupied/reserved
 * seats (409) so you must boot a player / revoke an invite first.
 */
export function useUpdateGameSettings(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateGameSettingsRequest) =>
      apiPatch<GameResponse>(`/api/v1/games/${gameId}/settings`, body),
    onSuccess: (game) => {
      setGameCache(qc, gameId, game)
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

/**
 * useRedeemCode — POST /api/v1/games/:id/redeem-code { code }
 *
 * Redeems a private-game 4-digit PIN on behalf of the authenticated viewer.
 * On 200 the response is the full GameResponse — we drop it straight into
 * the React-Query cache for this game so the LOBBY screen re-renders into
 * State B (slot picker) without a follow-up GET.
 *
 * Error shape reference:
 *   401 WRONG_CODE        — PIN is incorrect.
 *   400 BAD_REQUEST       — malformed PIN.
 *   409 GAME_FULL / NOT_LOBBY / ALREADY_PLAYER — surface the message and close the modal.
 *   404 NOT_FOUND         — no such game id.
 */
export function useRedeemCode(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: RedeemInviteCodeRequest) =>
      apiPost<RedeemInviteCodeResponse>(`/api/v1/games/${gameId}/redeem-code`, data),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data)
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

export function useUpdateMySlot(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateMySlotRequest) =>
      apiPatch<UpdateMySlotResponse>(`/api/v1/games/${gameId}/me/slot`, data),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data.game)
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

/**
 * useUpdateMyColor — PATCH /api/v1/games/:id/me/color { color }
 *
 * Lets a player change their color (decoupled from seat) while the game is in
 * LOBBY. On 409 COLOR_TAKEN the caller should surface a toast and refetch.
 */
export function useUpdateMyColor(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateMyColorRequest) =>
      apiPatch<UpdateMyColorResponse>(`/api/v1/games/${gameId}/me/color`, data),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data.game)
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

/**
 * useRevokeInvitation — DELETE /api/v1/games/:id/invitations/:invitationId
 *
 * Host-only. Removes a pending invitation from the game's pending list.
 * On success, invalidates the game detail query so the lobby pending-
 * invitations list re-renders.
 */
export function useRevokeInvitation(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiDelete<void>(`/api/v1/games/${gameId}/invitations/${invitationId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.detail(gameId) })
    },
  })
}

/**
 * useResendInvite — POST /api/v1/games/:id/invite { userId } for a user who
 * already holds a pending invite. The server keeps their reserved seat and
 * sends the push/email again (it refreshes sentAt; no second seat).
 */
export function useResendInvite(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      apiPost<{ invited: { id: string }; pushed: boolean; emailed: boolean }>(`/api/v1/games/${gameId}/invite`, { userId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.detail(gameId) })
    },
  })
}

/**
 * useDeclineInvitation — POST /api/v1/games/:id/decline
 *
 * The invited viewer turns down THEIR OWN pending invitation (self-serve
 * mirror of the host's revoke). The reserved seat opens immediately server-
 * side. Invalidates the games LIST too — a declined game must drop off the
 * viewer's home list (it was only there via the pending invite).
 */
export function useDeclineInvitation(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiPost<{ declined: boolean; invitationId: string }>(`/api/v1/games/${gameId}/decline`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.detail(gameId) })
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

export function useStartGame(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    // Caller passes the StartGameRequest body (map + config). When the
    // mutation is called with no args, `body` is undefined and the server
    // falls back to DEFAULT_MAP_ID + DEFAULT_CONFIG.
    mutationFn: (body?: { mapId?: string; config?: Partial<import('@warcarp/api-types').GameConfig> }) =>
      apiPost<GameResponse>(`/api/v1/games/${gameId}/start`, body ?? {}),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data)
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

export function useAdvancePhase(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost<GameResponse>(`/api/v1/games/${gameId}/turn/advance`),
    onSuccess: (data) => {
      // HTTP response is authoritative — write it immediately so the UI
      // transitions without waiting for any WS event. The new server (PR F)
      // returns post-HUMAN state here (bot's turn); the old prod server already
      // returns post-bot state. Either way this is the ground truth.
      setGameCache(qc, gameId, data)
      // setGameCache refreshes list membership if this advances the turn.
    },
  })
}

export function useCompleteGame(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost<GameResponse>(`/api/v1/games/${gameId}/complete`),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data)
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

export function useMarkReady(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost<GameResponse>(`/api/v1/games/${gameId}/ready`),
    onSuccess: (data) => setGameCache(qc, gameId, data),
  })
}

export function useResign(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiPost<GameResponse>(`/api/v1/games/${gameId}/resign`, {}),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data)
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

/** Abandon instead of resigning: an AI takes over the caller's seat for the rest of the game. */
export function useAbandonGame(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost<{ abandoned: true; botName: string | null }>(`/api/v1/games/${gameId}/abandon`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

export function useAddBot(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data?: AddBotRequest) =>
      apiPost<GameResponse>(`/api/v1/games/${gameId}/add-bot`, data ?? {}),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data)
      void qc.invalidateQueries({ queryKey: QK.list })
    },
  })
}

export function useClaim(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ClaimRequest) =>
      apiPost<GameResponse>(`/api/v1/games/${gameId}/turn/claim`, data),
    onSuccess: (data) => {
      // HTTP response is authoritative — write it immediately.
      // useClaim fires during the claim phase (game already ACTIVE); the
      // list invalidation it previously carried was a hold-over from before
      // PR C. Dropped here: claim doesn't change anything visible in the
      // lobby list, and triggering a list refetch cascades extra repaints on
      // slow connections for no user-visible gain.
      setGameCache(qc, gameId, data)
      // No list invalidation: in-game action, lobby list unaffected.
    },
  })
}

export function useReinforce(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ReinforceRequest) =>
      apiPost<GameResponse>(`/api/v1/games/${gameId}/turn/reinforce`, data),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data)
      // No list invalidation: reinforce doesn't change anything visible
      // in the lobby list. The invalidation was causing the lobby data
      // to flip into "fetching" state on slow connections, triggering
      // extra repaints.
    },
  })
}

/**
 * usePostElimPlace — commit post-elimination bonus army placement.
 *
 * Called during the 'post-elim-placement' phase. The budget is
 * state.bonusArmies[playerId] only (no base / region reinforcement).
 * On success the server flips phase back to 'attack'.
 */
export function usePostElimPlace(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: PostElimPlaceRequest) =>
      apiPost<GameResponse>(`/api/v1/games/${gameId}/turn/post-elim-place`, data),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data)
      // No list invalidation: in-game action, lobby list unaffected.
    },
  })
}

export function useAttack(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: AttackRequest) =>
      apiPost<AttackResponse>(`/api/v1/games/${gameId}/turn/attack`, data),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data.game)
      // No list invalidation: in-game action, lobby list unaffected.
    },
  })
}

export function useFortify(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: FortifyRequest) =>
      apiPost<GameResponse>(`/api/v1/games/${gameId}/turn/fortify`, data),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data)
      // No list invalidation: in-game action, lobby list unaffected.
    },
  })
}

/**
 * useTurnInCards — trade in a 3-card set during the placement phase.
 * Server validates the set, awards the schedule's bonus into
 * state.bonusArmies (which then flows through reinforcementCount on the
 * next render), and clears `mustTurnInCards` once the player drops to ≤4
 * cards. The placement HUD picks up the new budget automatically via the
 * game refetch in onSuccess.
 */
export function useTurnInCards(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: TurnInCardsRequest) =>
      apiPost<GameResponse>(`/api/v1/games/${gameId}/turn/turn-in-cards`, data),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data)
      // No list invalidation: in-game action, lobby list unaffected.
    },
  })
}

/**
 * usePostConquestTransfer — move additional armies from the conquering
 * source into the just-conquered territory while still in the attack
 * phase. Valid only while state.lastConquest is set. Does not consume
 * the per-turn fortify cap.
 */
export function usePostConquestTransfer(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: TransferRequest) =>
      apiPost<GameResponse>(`/api/v1/games/${gameId}/turn/transfer`, data),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data)
      // No list invalidation: in-game action, lobby list unaffected.
    },
  })
}

/**
 * useEndTurn — skip directly to the next player's turn.
 *
 * Calls advancePhase repeatedly until the turnIndex changes (i.e. the current
 * player's 3 phases are all done). Works regardless of which phase we're in.
 * Max 3 advances (reinforce→attack→fortify→next) to avoid infinite loops.
 */
export function useEndTurn(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      // Single /turn/advance call. On the current prod server the bot driver
      // fast-forwards past all bot players before returning the 200, so the
      // response already carries the next HUMAN's turn state. On the new
      // server (PR F / fix/decouple-bot-loop) the 200 returns immediately
      // with the post-HUMAN state (bot's turn); bots play asynchronously
      // via GAME_STATE_PATCH → GAME_UPDATED. Either way:
      //   • onSuccess writes the response to cache immediately (authoritative).
      //   • useBotSafetyTimeout (see hooks/useBotSafetyTimeout.ts) detects if
      //     the response shows a bot's turn and arms a 30s refetch fallback so
      //     the client can never get permanently stuck waiting for a WS event.
      apiPost<GameResponse>(`/api/v1/games/${gameId}/turn/advance`),
    onSuccess: (data) => {
      // HTTP response is authoritative. Write it now — do not wait on WS.
      // WS GAME_UPDATED / GAME_STATE_PATCH events are additive optimizations:
      // they arrive if the socket is healthy and provide bot-step animation,
      // but their absence must never leave the client in a stuck pending state.
      setGameCache(qc, gameId, data)
      // Human and bot handoffs both change the menu's Your Turn membership.
      // setGameCache refreshes the shared list at that boundary.
    },
  })
}

/**
 * useReturnToAttack — revert from the fortify phase back to the attack phase.
 *
 * Only valid when fortifiesThisTurn === 0. The server returns 409 if a fortify
 * has already been committed. The button in FortifyUI is shown only while
 * state.fortifiesThisTurn === 0 so the 409 path is defensive.
 */
export function useReturnToAttack(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiPost<ReturnToAttackResponse>(`/api/v1/games/${gameId}/turn/return-to-attack`, {}),
    onSuccess: (data) => {
      setGameCache(qc, gameId, data)
      // No list invalidation: in-game action, lobby list unaffected.
    },
  })
}
